import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setTotalAmount,
  setTaxSlab,
  setGstType,
  setGstValue,
  resetGstData,
} from "../redux/calculations/gstSlice";
import {
  updatedGstChart,
} from "../redux/calculations/calculaterSlice";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import arrowImg from "../assets/images/down.png";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";

const GstCalc = ({ setCalcDone }) => {
  const dispatch = useDispatch();
  const { totalAmount, taxSlab, gstType } = useSelector(
    (state) => state.gstCalculator
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);

  const options = ["0.25", "3", "5", "12", "18", "28"];

  const { textData } = useLocale();
  const { gstCalc } = textData;

  const selectedSlab = taxSlab || "Select Tax Slab";
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const [selectedOption, setSelectedOption] = useState("excluding");

  const formik = useFormik({
    initialValues: {
      totalAmount: totalAmount,
      taxSlab: taxSlab || "",
      gstType: gstType,
    },
    validationSchema: Yup.object({
      totalAmount: Yup.number()
        .min(5000, gstCalc.totalAmountMin)
        .max(1000000, gstCalc.totalAmountMax)
        .required(gstCalc.totalAmountRequired),
      taxSlab: Yup.string().required(gstCalc.gstRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_GST");
      }

      const amount = parseFloat(values.totalAmount);
      const taxRate = parseFloat(values.taxSlab) / 100;

      let gstAmount, principleAmount, totalWithGst;

      if (values.gstType === "excluding") {
        gstAmount = amount * taxRate;
        principleAmount = amount;
        totalWithGst = amount + gstAmount;
      } else {
        principleAmount = amount / (1 + taxRate);
        gstAmount = amount - principleAmount;
        totalWithGst = amount;
      }

      dispatch(setTotalAmount(values.totalAmount));
      dispatch(setTaxSlab(values.taxSlab));
      dispatch(setGstType(values.gstType));
      dispatch(setGstValue(gstAmount));

      dispatch(
        updatedGstChart({
          totalPrinciple: principleAmount,
          gstAmmount: gstAmount,
          totalAmount: totalWithGst,
        })
      );      

      if (setCalcDone) {
        setCalcDone(true);
      }
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetGstData());
    };
  }, [dispatch]);

  const handleSelect = (option) => {
    dispatch(setTaxSlab(option));
    formik.setFieldValue("taxSlab", option);
    setIsOpen(false);
  };

  useEffect(() => {
    dispatch(setTotalAmount(formik.values.totalAmount));
    dispatch(setTaxSlab(formik.values.taxSlab));
    dispatch(setGstType(formik.values.gstType));
  }, [formik.values, dispatch]);

  const sliderTrackRef = useRef(null);

  const handleTotalAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTotalAmount(value));
    formik.setFieldValue("totalAmount", value);
    setCalcDone(false);
  };

  const handleGstTypeChange = (type) => {
    setSelectedOption(type);
    formik.setFieldValue("gstType", type);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";
  
    const shouldForceIndianFormat = i18n.language === "fa";
  
    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
          shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
        );
  };
  

  const handleAmountChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue === "") {
      formik.setFieldValue("totalAmount", "");
      return;
    }
    const rawValue = inputValue.replace(/[^0-9]/g, "");
    if (rawValue.length > 6) return;

    if (rawValue === "0") {
      formik.setFieldValue("totalAmount", 0);
      return;
    }
    formik.setFieldValue("totalAmount", rawValue ? Number(rawValue) : "");
    setCalcDone(false);
  };

  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };
  
  return (
    <>
      <div className="pb-2 sm:pb-6">
        <div className="flex items-center my-6 gap-5 sm:gap-8 ">
          {/* Excluding GST */}
          <div className="flex items-center">
            <label
              htmlFor="excluding-gst"
              className="gap-1 sm:gap-[10px] text-black flex text-sm sm:text-sm font-normal item-center text-start"
            >
              <input
                type="radio"
                id="excluding-gst"
                name="gst"
                value="excluding"
                checked={selectedOption === "excluding"}
                onChange={() => handleGstTypeChange("excluding")}
                className="sr-only"
              />
              <div
                className={`h-5 w-5 rounded-full border ${
                  selectedOption === "excluding"
                    ? "border-[#07B5B5] bg-white"
                    : "border-[#07B5B5]"
                } flex items-center justify-center`}
              >
                {selectedOption === "excluding" && (
                  <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                )}
              </div>

              <span> {textData.gstCalc.excludingGstLabel}</span>
            </label>
          </div>

          <div className="flex items-center">
            <label
              htmlFor="including-gst"
              className="gap-1 sm:gap-[10px] text-black  flex text-sm sm:text-sm items-center font-normal text-start"
            >
              <input
                type="radio"
                id="including-gst"
                name="gst"
                value="including"
                checked={selectedOption === "including"}
                onChange={() => handleGstTypeChange("including")}
                className="sr-only"
              />
              <div
                className={`h-5 w-5 rounded-full border ${
                  selectedOption === "excluding"
                    ? "border-[#07B5B5] bg-white"
                    : "border-[#07B5B5]"
                } flex items-center justify-center`}
              >
                {selectedOption === "including" && (
                  <div className="h-3 w-3 rounded-full bg-[#07B5B5]"></div>
                )}
              </div>
              <span> {textData.gstCalc.includingGstLabel}</span>{" "}
            </label>
          </div>
        </div>
        <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
          <div >
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.gstCalc.totalAmountLabel}
              </label>
            </div>
            <div className="flex justify-between gap-5 items-center">
              <div className="relative w-full" ref={sliderTrackRef}>
                
                <input
                  type="range"
                  min={5000}
                  max={1000000}
                  step={100}
                  value={totalAmount}
                  onChange={handleTotalAmountSliderChange}
                  className="flex-1 min-w-0 max-w-[314px]"
                  style={{
                    width: "100%",
                    appearance: "none",
                    background: "transparent",
                    ...(isRTL() && {
                      direction: "ltr",
                    }),
                    "--val": `${
                      ((totalAmount - 5000) / (1000000 - 5000)) * 100
                    }%`,
                  }}
                />
              </div>{" "}
              <div className="flex items-center flex-col">
                <Input
                  name="totalAmount"
                  leftIcon={textData.currency}
                  value={formatToINR(formik.values.totalAmount)}
                  onChange={handleAmountChange}
                  onBlur={formik.handleBlur}
                  type="text"
                    dir="ltr"
                  placeholder={getPlaceholderValue(i18n.language)}
                  borderColor={
                    formik.touched.totalAmount && formik.errors.totalAmount
                      ? "border-red-500"
                      : "border-gray-light"
                  }
                  className={` sm:h-[36px] h-[32px]  ${
                    formik.touched.totalAmount && formik.errors.totalAmount
                      ? "border-danger"
                      : "border-gray-light"
                  }  flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                />
                {formik.touched.totalAmount && formik.errors.totalAmount && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.totalAmount}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div >
            <div className="flex justify-between items-center mb-2 gap-4">
              <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                {textData.gstCalc.taxslab}
              </label>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="border border-gray-light sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                >
                  <span className="text-sm sm:text-[15px]">{selectedSlab}</span>

                  <div
                    className={`pointer-events-none absolute inset-y-0  flex items-center pr-[18px] ${
                      isRTL() ? "left-[12px]" : "right-0"
                    }`}
                  >
                    <img
                      src={arrowImg}
                      alt="Dropdown arrow"
                      className={` h-4 w-4 object-contain transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div className="absolute z-10 top-9 sm:top-12 w-full mt-1 bg-white rounded-md shadow-lg border border-solid border-primary h-[153px] overflow-y-auto">
                    <ul className="py-1">
                      {options.map((option) => (
                        <li key={option}>
                          <button
                            type="button"
                            className={`block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm sm:text-sm ${
                              taxSlab === option ? "bg-gray-light" : ""
                            }`}
                            onClick={() => handleSelect(option)}
                          >
                            {option}%
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {formik.touched.taxSlab && formik.errors.taxSlab && (
                  <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                    {formik.errors.taxSlab}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-[10px]">
            <Button type="submit" className="w-full text-white">
              {textData.calculate}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default GstCalc;
